import { Form } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";

import type { TerminalProfileEntity } from "src/app/features/TerminalProfile/types/TerminalProfileEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth/SecurityManager";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import type { Entity } from "../../../../core/types/fe/Entity";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { useLoadGenericFormQuery } from "../../services/server/formComponentsApi";
import type { AntCustomFormField, QueryParams } from "../../types/types";

interface ParameterItem extends Entity {
    name: string;
    value: string;
}

export interface ParametersTableProps extends AntCustomFormField<number> {}

const propertiesToShow = [
    "defaultEmvKernelId",
    "emvTerminalType",
    "terminalCapabilities",
    "addTerminalCapabilities",
    "insertTimeout",
    "insertTimeoutCless",
    "maxIccTries",
    "minMsrTries",
    "maxMsrTries",
    "merchantCategoryCode",
    "pinEntryTimeout",
    "matAllowedCVMList",
    "languageList",
    "terminalCurrencyCode",
    "maxLogSize",
    "maxJournalAge",
    "integratedPrinting",
    "colorScheme",
    "financialTransactions",
    "administrationTransactions",
    "offlineMode",
    "offlineTransactions",
    "offlineMaxCount",
    "additionalSound",
    "displayTimeMillis",
    "confirmationTimeMillis",
    "displayResultTimeMillis",
    "partialApprovalConfirmOnScreen",
    "stanStep",
    "stanMin",
    "stanMax",
    "deferredModePrompt",
    "cardPresentRelatedTransactions",
    "rklConnectionString",
    "rklServerCommonName",
    "receiptTemplate",
    "forceSignatureOnTerminal",
    "merchantPreferenceOverride",
    "declinedHostMessageEnabled",
];

export const TerminalsParametersTable: FC<ParametersTableProps> = () => {
    const [processedData, setProcessedData] = useState<ParameterItem[]>([]);

    const form = Form.useFormInstance();
    const dataGroup = getAssignedDataGroup();

    const terminalProfileId = form?.getFieldValue?.("terminalProfileId");

    const queryParams: QueryParams = {
        resource: ResourceName.TERMINAL_PROFILES,
        dataGroupId: dataGroup?.code,
        entityId: terminalProfileId ?? undefined,
    };

    const { data, isFetching } = useLoadGenericFormQuery(queryParams, { skip: !queryParams.entityId });

    useEffect(() => {
        const inputData: TerminalProfileEntity = { ...data };
        let processedInputData: ParameterItem[] = [];

        propertiesToShow.forEach((prop) => {
            // @ts-ignore
            let inputVal = inputData[prop];
            if (typeof inputVal === "boolean") {
                inputVal = inputVal ? "true" : "false";
            }
            const item: ParameterItem = {
                id: prop,
                name: prop,
                value: inputVal,
            } as ParameterItem;

            processedInputData = [...processedInputData, item];
        });

        setProcessedData(processedInputData);
    }, [data, setProcessedData]);

    const getColumnsConfig = (t: Function): ExtendedColumnsType<ParameterItem>[] => {
        return [
            {
                title: t("list.form.name"),
                key: "name",
                dataIndex: "name",
                render: (_, record) => t(`terminalProfileCreateForm.${record.name}`),
            },
            {
                title: t("list.form.value"),
                key: "value",
                dataIndex: "value",
            },
        ];
    };

    return (
        (queryParams.entityId && (
            <PropertyTable<ParameterItem>
                rowsAreSelectable={false}
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="id"
                isFetching={isFetching}
                tableData={processedData}
                readOnly={false}
                pagination={false}
            />
        )) ||
        null
    );
};
